import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiErr } from "@/lib/api";
import { fmtDateTime, num, locString } from "@/lib/format";
import { newClientId } from "@/lib/smartInventory";
import { ProtectedImage } from "@/components/ProtectedImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Camera, Search, Minus, Plus, Check, X, ImagePlus, AlertTriangle } from "lucide-react";

const NO_LOCATION = "__none__";

function Scanner({ onCode, onClose }) {
  const ref = useRef(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let scanner = null, started = false, done = false;
    const start = async () => {
      try {
        scanner = new Html5Qrcode("inv-qr-reader");
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: { width: 230, height: 230 } },
          (text) => { if (!done) { done = true; onCode(text); } }, () => {});
        started = true;
      } catch { setError("Não foi possível abrir a câmera. Digite o código."); }
    };
    start();
    return () => { done = true; (async () => { try { if (started) await scanner.stop(); scanner?.clear(); } catch { /* câmera já liberada */ } })(); };
  }, [onCode]);
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/90 p-4">
      <div className="mb-3 flex justify-between text-white"><span>Aponte para o QR Code ou código de barras</span><button type="button" onClick={onClose} aria-label="Fechar"><X className="h-6 w-6" /></button></div>
      <div id="inv-qr-reader" ref={ref} className="mx-auto w-full max-w-sm overflow-hidden rounded-xl" />
      {error && <p className="mt-4 text-center text-amber-300">{error}</p>}
    </div>
  );
}

export default function InventoryCount() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState(() => { try { return localStorage.getItem(`inv-loc-${id}`) || NO_LOCATION; } catch { return NO_LOCATION; } });
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);
  const [qty, setQty] = useState("");
  const [obs, setObs] = useState("");
  const [photo, setPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mode, setMode] = useState("NOVA");
  const [busy, setBusy] = useState(false);
  const [scan, setScan] = useState(false);
  const [recent, setRecent] = useState([]);
  const clientIdRef = useRef(null);
  const searchRef = useRef(null);
  const qtyRef = useRef(null);

  useEffect(() => {
    api.get(`/inventories/${id}/v2`).then((r) => setInv(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/locations").then((r) => setLocations(r.data || [])).catch(() => setLocations([]));
  }, [id]);
  useEffect(() => { try { localStorage.setItem(`inv-loc-${id}`, location); } catch { /* sem armazenamento local */ } }, [id, location]);

  const search = useCallback(async (text, auto = false) => {
    const term = (text ?? q).trim();
    if (!term) { setResults(null); return; }
    try {
      const r = await api.get(`/inventories/${id}/lookup`, { params: { q: term } });
      setResults(r.data);
      if (auto && r.data.length === 1) pick(r.data[0]);
    } catch (e) { toast.error(apiErr(e)); }
  }, [id, q]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const t = setTimeout(() => { if (q.trim().length >= 3) search(); }, 400); return () => clearTimeout(t); }, [q, search]);

  const pick = (part) => {
    setSelected(part); setQty(""); setObs(""); setPhoto(""); setMode("NOVA");
    clientIdRef.current = newClientId();
    setTimeout(() => qtyRef.current?.focus(), 50);
  };
  const locId = location === NO_LOCATION ? null : location;
  const here = (selected?.contagens || []).filter((c) => (c.localizacao_id || null) === locId);

  const onScan = useCallback((text) => { setScan(false); setQ(text); search(text, true); }, [search]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/upload", fd); setPhoto(r.data.path); toast.success("Foto anexada."); }
    catch (err) { toast.error(apiErr(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (qty === "" || Number(qty) < 0 || !Number.isInteger(Number(qty))) { toast.error("Informe a quantidade encontrada (número inteiro)."); return; }
    if (here.length && mode === "NOVA") { toast.error("Já existe contagem desta peça neste local. Escolha somar ou substituir."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/inventories/${id}/counts`, {
        part_id: selected.id, quantidade_fisica: Number(qty), localizacao_id: locId || undefined,
        observacao: obs || undefined, foto: photo || undefined, modo: mode, client_id: clientIdRef.current,
      });
      toast.success(r.data.repetida ? "Esta contagem já tinha sido salva." : `Contagem salva: ${qty} ${selected.unidade || ""}`);
      setRecent((list) => [{ ...r.data.count, desc: selected.descricao }, ...list].slice(0, 15));
      setSelected(null); setResults(null); setQ("");
      setTimeout(() => searchRef.current?.focus(), 50);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (!inv) return <div className="p-6 text-slate-400">Carregando...</div>;
  const closed = !["ABERTO", "EM_CONTAGEM", "AGUARDANDO_CONFERENCIA"].includes(inv.status);

  return (
    <div className="mx-auto max-w-xl space-y-4 pb-24">
      <Link to={`/inventario/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-400"><ArrowLeft className="h-4 w-4" />{inv.descricao}</Link>
      {closed && <p className="rounded-lg bg-rose-950/60 p-3 text-sm text-rose-200">Este inventário está {inv.status_label?.toLowerCase()}. Não é possível contar.</p>}
      {inv.status === "AGUARDANDO_CONFERENCIA" && <p className="rounded-lg bg-amber-950/60 p-3 text-sm text-amber-200">Contagem encerrada: só as peças marcadas para recontagem podem ser contadas.</p>}

      <div className="sticky top-0 z-10 space-y-2 rounded-xl border border-slate-800 bg-slate-950/95 p-3 backdrop-blur">
        <Label className="text-xs text-slate-400">Estou contando em</Label>
        <Select value={location} onValueChange={setLocation}>
          <SelectTrigger className="h-12 bg-slate-900 border-slate-700 text-base"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-72">
            <SelectItem value={NO_LOCATION}>Sem localização definida</SelectItem>
            {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.nome || locString(l)}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <div className="relative flex-1"><Search className="absolute left-3 top-3.5 h-5 w-5 text-slate-500" />
            <Input ref={searchRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") search(undefined, true); }}
              placeholder="Código, descrição ou código antigo" className="h-12 pl-10 bg-slate-900 border-slate-700 text-base" data-testid="count-search" /></div>
          <Button type="button" onClick={() => setScan(true)} className="h-12 bg-sky-700 px-4" aria-label="Ler QR Code"><Camera className="h-5 w-5" /></Button>
        </div>
      </div>

      {!selected && results && (
        <div className="space-y-2">
          {results.length === 0 && <p className="p-4 text-center text-slate-500">Nenhuma peça encontrada.</p>}
          {results.map((p) => (
            <button key={p.id} type="button" onClick={() => pick(p)} className="w-full rounded-xl border border-slate-800 bg-slate-900 p-3 text-left active:bg-slate-800">
              <div className="flex items-center justify-between gap-2"><span className="font-mono text-sky-300">{p.codigo_interno}</span>
                {p.linha_status === "APROVADO" ? <span className="text-xs text-sky-300">já aprovada</span> : p.contagens.length > 0 ? <span className="text-xs text-emerald-300">{p.contagens.length} contagem(ns)</span> : null}</div>
              <div className="text-slate-100">{p.descricao}</div>
              <div className="text-xs text-slate-500">{[p.fabricante, p.unidade, p.localizacao].filter(Boolean).join(" · ")}{!p.no_escopo ? " · fora do escopo" : ""}</div>
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="space-y-4 rounded-xl border border-sky-800 bg-slate-900 p-4">
          <div className="flex gap-3">
            {selected.foto_principal && <ProtectedImage src={selected.foto_principal} alt="" className="h-20 w-20 shrink-0 rounded-lg object-cover" />}
            <div className="min-w-0">
              <div className="font-mono text-lg text-sky-300">{selected.codigo_interno}</div>
              <div className="text-lg font-semibold text-slate-50">{selected.descricao}</div>
              <div className="text-sm text-slate-400">{[selected.fabricante, selected.unidade].filter(Boolean).join(" · ")}</div>
              {selected.localizacao && <div className="text-xs text-slate-500">Cadastro: {selected.localizacao}</div>}
            </div>
          </div>
          {selected.lido_como && <p className="flex gap-2 rounded-lg bg-amber-950/60 p-2 text-sm text-amber-200"><AlertTriangle className="h-4 w-4 shrink-0" />O código {selected.lido_como} foi consolidado neste cadastro. Conte tudo junto aqui.</p>}
          {selected.codigos_do_grupo?.length > 1 && <p className="text-xs text-slate-400">Mesma peça com os códigos: {selected.codigos_do_grupo.join(", ")}. Conte uma vez só.</p>}
          {selected.linha_status === "APROVADO" && <p className="rounded-lg bg-sky-950/60 p-2 text-sm text-sky-200">Esta peça já foi aprovada neste inventário.</p>}
          {selected.contagens.length > 0 && <div className="space-y-1 text-sm">
            <div className="text-xs uppercase text-slate-500">Já contado neste inventário</div>
            {selected.contagens.map((c) => <div key={c.id} className={(c.localizacao_id || null) === locId ? "text-amber-300" : "text-slate-400"}>
              {num(c.quantidade_fisica)} em {c.localizacao_nome || "sem localização"} · {c.usuario_nome} · {fmtDateTime(c.data)}</div>)}
          </div>}
          {here.length > 0 && <div className="space-y-2 rounded-lg border border-amber-700 bg-amber-950/40 p-3 text-sm">
            <div className="font-semibold text-amber-200">Esta peça já foi contada neste local.</div>
            <label className="flex items-center gap-2 text-slate-100"><input type="radio" name="mode" checked={mode === "SOMAR"} onChange={() => setMode("SOMAR")} />Somar: é outro lote/caixa no mesmo local</label>
            <label className="flex items-center gap-2 text-slate-100"><input type="radio" name="mode" checked={mode === "SUBSTITUIR"} onChange={() => setMode("SUBSTITUIR")} />Substituir a contagem anterior</label>
          </div>}
          <div>
            <Label htmlFor="count-qty" className="text-slate-300">Quantidade encontrada</Label>
            <div className="mt-1 flex items-center gap-2">
              <Button type="button" variant="outline" onClick={() => setQty(String(Math.max(0, (Number(qty) || 0) - 1)))} className="h-14 w-14 border-slate-700 bg-slate-800" aria-label="Menos um"><Minus className="h-5 w-5" /></Button>
              <Input id="count-qty" ref={qtyRef} type="number" inputMode="numeric" min="0" step="1" value={qty} onChange={(e) => setQty(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") save(); }} className="h-14 flex-1 bg-slate-950 border-slate-600 text-center text-2xl font-bold" data-testid="count-qty" />
              <Button type="button" variant="outline" onClick={() => setQty(String((Number(qty) || 0) + 1))} className="h-14 w-14 border-slate-700 bg-slate-800" aria-label="Mais um"><Plus className="h-5 w-5" /></Button>
            </div>
            <p className="mt-1 text-xs text-slate-500">Não achou nenhuma? Informe 0: fica registrado como “não encontrado”.</p>
          </div>
          <div><Label htmlFor="count-obs" className="text-slate-300">Observação (opcional)</Label>
            <Input id="count-obs" maxLength={500} value={obs} onChange={(e) => setObs(e.target.value)} className="mt-1 bg-slate-950 border-slate-700" /></div>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
            <ImagePlus className="h-5 w-5" />{uploading ? "Enviando foto..." : photo ? "Foto anexada (trocar)" : "Anexar foto (opcional)"}
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={upload} disabled={uploading} />
          </label>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => setSelected(null)} className="h-12 flex-1">Cancelar</Button>
            <Button type="button" disabled={busy || uploading || closed || selected.linha_status === "APROVADO"} onClick={save} className="h-12 flex-[2] bg-emerald-700 text-base text-white hover:bg-emerald-600" data-testid="count-save">
              <Check className="h-5 w-5 mr-1" />{busy ? "Salvando..." : "Salvar contagem"}</Button>
          </div>
        </div>
      )}

      {recent.length > 0 && !selected && <div className="space-y-1">
        <div className="text-xs uppercase text-slate-500">Minhas últimas contagens</div>
        {recent.map((c) => <div key={c.id} className="flex justify-between rounded-lg bg-slate-900 px-3 py-2 text-sm"><span className="truncate text-slate-200"><span className="font-mono text-sky-300">{c.codigo_interno}</span> {c.desc}</span><span className="font-mono text-slate-100">{num(c.quantidade_fisica)}</span></div>)}
      </div>}

      {scan && <Scanner onCode={onScan} onClose={() => setScan(false)} />}
    </div>
  );
}
